import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Check } from "lucide-react";
import { useSeo } from "@/lib/useSeo";
import { PMN_CLIENT_ENGAGEMENTS, LEADERSHIP_EXPERIENCE } from "@/data/site";
import { SEO } from "@/data/seo";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/motion/Reveal";
import { ParallaxImage } from "@/components/motion/ParallaxImage";
import { Button, Eyebrow } from "@/components/ui-kit";
import { CTASection } from "@/components/CTASection";
import { IMG, CASE_STUDIES_DETAIL } from "@/data/content";

function IndustryBlock({ item, index }) {
  const flip = index % 2 === 1;
  return (
    <section id={item.id} className="scroll-mt-24 border-t border-charcoal/10 py-20 first:border-t-0 lg:py-28">
      <div className="mx-auto max-w-8xl px-6 lg:px-10">
        {/* Header + what we solve */}
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
          <Reveal className={`lg:col-span-6 ${flip ? "lg:order-2" : ""}`}>
            <div className="relative h-[320px] lg:h-[440px]">
              <div className={`absolute ${flip ? "-right-4" : "-left-4"} -top-4 h-full w-full border border-teal/25`} />
              <ParallaxImage src={item.img} alt={item.name} className="relative h-full w-full" intensity={50} />
            </div>
          </Reveal>

          <Reveal delay={0.1} className={`lg:col-span-6 ${flip ? "lg:order-1" : ""}`}>
            <Eyebrow>{item.tagline}</Eyebrow>
            <h2 className="mt-5 text-3xl font-normal leading-[1.12] tracking-tightish text-charcoal sm:text-4xl lg:text-5xl">
              {item.name}
            </h2>
            <p className="mt-6 text-base leading-[1.8] text-charcoal/70">{item.intro}</p>

            <p className="mt-8 text-xs uppercase tracking-mega text-teal-deep">What PMN leads here</p>
            <ul className="mt-4 grid gap-2 sm:grid-cols-2">
              {item.leads.map((l) => (
                <li key={l} className="flex items-start gap-2 text-sm text-charcoal/75">
                  <span className="mt-1.5 h-1 w-1 shrink-0 rotate-45 bg-teal" />
                  {l}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>

        {/* Common problems */}
        <Reveal>
          <div className="mt-14 border border-charcoal/10 bg-paper-warm p-8 lg:p-10">
            <p className="text-xs uppercase tracking-mega text-charcoal/45">What we've helped solve</p>
            <div className="mt-6 grid gap-x-10 gap-y-4 sm:grid-cols-2">
              {item.problems.map((p) => (
                <div key={p} className="flex items-start gap-3 text-sm leading-relaxed text-charcoal/75">
                  <Check className="mt-1 h-4 w-4 shrink-0 text-teal" strokeWidth={1.5} />
                  <span>{p}</span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        {/* Case study */}
        {item.study ? (
          <Reveal>
            <div className="mt-6 bg-charcoal p-8 text-paper lg:p-12">
              <p className="text-xs uppercase tracking-mega text-teal-light">Founder-led engagement · prior employer</p>
              <h3 className="mt-5 max-w-3xl font-serif text-2xl leading-snug tracking-tightish sm:text-3xl">
                {item.study.title}
              </h3>

              <div className="mt-10 grid gap-10 lg:grid-cols-3">
                <div>
                  <p className="text-xs uppercase tracking-mega text-paper/40">The challenge</p>
                  <p className="mt-4 text-sm leading-[1.8] text-paper/70">{item.study.challenge}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-mega text-paper/40">What was done</p>
                  <ul className="mt-4 space-y-2.5">
                    {item.study.approach.map((a) => (
                      <li key={a} className="flex items-start gap-2 text-sm leading-relaxed text-paper/70">
                        <span className="mt-1.5 h-1 w-1 shrink-0 rotate-45 bg-teal-light" />
                        {a}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-mega text-paper/40">The outcome</p>
                  <p className="mt-4 text-sm leading-[1.8] text-paper/70">{item.study.outcome}</p>
                </div>
              </div>

              <div className={`mt-10 grid grid-cols-2 gap-px overflow-hidden border border-paper/15 bg-paper/15 ${item.study.metrics.length === 3 ? "sm:grid-cols-3" : "sm:grid-cols-4"}`}>
                {item.study.metrics.map(([v, l]) => (
                  <div key={l} className="bg-charcoal p-6">
                    <p className="font-serif text-3xl tracking-tightish text-teal-light lg:text-4xl">{v}</p>
                    <p className="mt-2 text-xs leading-tight text-paper/55">{l}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        ) : (
          <Reveal>
            <div className="mt-6 border border-teal/20 bg-paper p-8 lg:p-10">
              <p className="text-xs uppercase tracking-mega text-teal-deep">Engagements available</p>
              <p className="mt-4 max-w-3xl text-base leading-[1.8] text-charcoal/70">
                We're actively taking on telecom program leadership. Reach out to discuss a multi-site
                rollout, migration, or service-operations initiative — and we'll walk you through
                directly relevant delivery experience.
              </p>
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
}

export default function CaseStudies() {
  useSeo({ ...SEO["/case-studies"], image: IMG.boardroomTable, path: "/case-studies" });

  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => {
          window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - 90, behavior: "smooth" });
        }, 400);
      }
    }
  }, [location.hash]);

  return (
    <>
      <PageHero
        eyebrow="Case Studies"
        titleLines={["Programs led,", "stabilized, and shipped."]}
        subtitle="Two distinct records: engagements delivered by PMN for its clients, and the leadership experience of PMN's founder that the firm's model is built on."
        image={IMG.boardroomTable}
      />

      {/* Section 1 — PMN client engagements */}
      <section id="pmn-client-engagements" className="bg-paper py-24 lg:py-32" data-testid="pmn-client-engagements">
        <div className="mx-auto grid max-w-8xl gap-10 px-6 lg:grid-cols-12 lg:px-10">
          <Reveal className="lg:col-span-6">
            <Eyebrow>{PMN_CLIENT_ENGAGEMENTS.eyebrow}</Eyebrow>
            <h2 className="mt-6 max-w-xl text-3xl font-normal leading-[1.15] tracking-tightish text-charcoal sm:text-4xl lg:text-5xl">
              {PMN_CLIENT_ENGAGEMENTS.title}
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-6 lg:pt-14">
            <p className="text-base leading-[1.8] text-charcoal/70">{PMN_CLIENT_ENGAGEMENTS.body}</p>
            <Button to="/contact" className="mt-8" track="primary" data-testid="casestudies-contact-btn">Discuss an Initiative</Button>
          </Reveal>
        </div>
      </section>

      {/* Section 2 — Leadership experience behind PMN */}
      <section id="leadership-experience" className="bg-charcoal py-20 text-paper lg:py-28" data-testid="leadership-experience">
        <div className="mx-auto max-w-8xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow dark>{LEADERSHIP_EXPERIENCE.eyebrow}</Eyebrow>
            <h2 className="mt-6 max-w-2xl text-3xl font-normal leading-[1.15] tracking-tightish sm:text-4xl lg:text-5xl">{LEADERSHIP_EXPERIENCE.title}</h2>
            <p className="mt-6 max-w-2xl text-base leading-[1.8] text-paper/75" data-testid="leadership-attribution">{LEADERSHIP_EXPERIENCE.attribution}</p>
            <p className="mt-3 max-w-2xl text-sm leading-[1.8] text-paper/55">{LEADERSHIP_EXPERIENCE.note}</p>
          </Reveal>
        </div>
      </section>

      {/* Industry quick-nav */}
      <div className="sticky top-[68px] z-30 border-b border-charcoal/10 bg-paper/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-8xl gap-6 overflow-x-auto px-6 py-4 lg:px-10">
          {CASE_STUDIES_DETAIL.map((c) => (
            <a
              key={c.id}
              href={`#${c.id}`}
              className="whitespace-nowrap text-sm tracking-wideish text-charcoal/60 transition-colors hover:text-teal link-underline"
              data-testid={`casestudy-nav-${c.id}`}
            >
              {c.name}
            </a>
          ))}
        </div>
      </div>

      <div className="bg-paper">
        {CASE_STUDIES_DETAIL.map((item, i) => (
          <IndustryBlock key={item.id} item={item} index={i} />
        ))}
      </div>

      <CTASection
        eyebrow="Your program next"
        title="Have an initiative that can't fail?"
        body="Tell us what's driving it — a program at risk, a regulatory commitment, a PMO stand-up, or a post-acquisition integration — and we'll tell you straight whether PMN can help."
        image={IMG.boardroomWarm}
      />
    </>
  );
}
